<template>
  <v-row class="px-4">
    <v-col cols="12" class="text-center font-weight-bold text-h6">
      Статистика по срокам обработки писем на период с {{ startFilterDisplayValue }} по {{ endFilterDisplayValue }}
    </v-col>

    <v-col cols="12" md="4" class="d-print-none">
      <v-menu
          v-model="filterStartMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
              v-model="startFilterDisplayValue"
              label="Начало периода"
              prepend-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
          ></v-text-field>
        </template>
        <v-date-picker
            v-model="filters.start"
            @input="filterStartMenu = false"
            @change="onFilterStartChanged"
            no-title
            scrollable
            locale="ru"
            first-day-of-week="1"
        ></v-date-picker>
      </v-menu>
    </v-col>
    <v-col cols="12" md="4" class="d-print-none">
      <v-menu
          v-model="filterEndMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
              v-model="endFilterDisplayValue"
              label="Конец периода"
              prepend-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
          ></v-text-field>
        </template>
        <v-date-picker
            v-model="filters.end"
            @input="filterEndMenu = false"
            @change="onFilterEndChanged"
            no-title
            scrollable
            locale="ru"
            first-day-of-week="1"
        ></v-date-picker>
      </v-menu>
    </v-col>

    <v-col cols="12" md="4" class="d-flex align-center justify-center justify-md-end d-print-none">
      <v-btn @click="onRefreshClicked" color="primary" large class="me-2">Обновить</v-btn>
      <v-btn @click="onPrintClicked" color="secondary" large>Распечатать</v-btn>
    </v-col>

    <v-col cols="12">
      <v-simple-table dense>
        <template v-slot:default>
          <table class="report-table">
            <thead>
            <tr>
              <th class="text-left">Организации</th>
              <th v-for="range in timeRanges" :key="range.key" class="text-center">{{ range.label }}</th>
              <th class="text-center">Общее</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="item in processedItems" :key="item.clientType">
              <td class="text-left font-weight-medium">
                {{ item.clientName }}
              </td>
              <td v-for="range in timeRanges" :key="range.key" class="text-center">
                {{ getCount(item, range.key) }}
              </td>
              <td class="text-center font-weight-bold">{{ getRowTotal(item) }}</td>
            </tr>

            <tr class="total-row">
              <td class="text-left font-weight-bold">Общее</td>
              <td v-for="range in timeRanges" :key="range.key" class="text-center font-weight-bold">
                {{ totalsByTimeRange[range.key] || 0 }}
              </td>
              <td class="text-center font-weight-black">{{ grandTotal }}</td>
            </tr>
            </tbody>
          </table>
        </template>
      </v-simple-table>
    </v-col>
  </v-row>
</template>

<script>
import Moment from 'moment';
import * as clients from '@/helpers/constants';

export default {
  name: "PagesSumRegionReportComponent",
  props: {
    items: {
      type: [Object, Array],
      default: () => ({})
    }
  },
  data() {
    const start = Moment().subtract(1, 'days');
    const end = Moment().subtract(1, 'days');

    return {
      filters: {
        start: start.format('YYYY-MM-DD'),
        end: end.format('YYYY-MM-DD'),
      },
      startFilterDisplayValue: start.format('DD.MM.YYYY'),
      endFilterDisplayValue: end.format('DD.MM.YYYY'),
      filterStartMenu: false,
      filterEndMenu: false,
      timeRanges: [
        { key: 'Received', label: 'Принято' },
        { key: 'ZeroToThree', label: '0-3' },
        { key: 'ThreeToTen', label: '3-10' },
        { key: 'TenToTwenty', label: '10-20' },
        { key: 'TwentyToFifty', label: '20-50' },
        { key: 'FiftyToOneHundred', label: '50-100' },
        { key: 'OneHundredPlus', label: '100+' },
      ],
    };
  },
  computed: {
    allClients() {
      return Object.values(clients);
    },

    processedItems() {
      if (!this.items) return [];
      if (Array.isArray(this.items) && this.items.length === 0) return [];
      if (Object.keys(this.items).length === 0) return [];

      const clientsData = {};

      for (const timeRangeKey in this.items) {
        const dataForRange = this.items[timeRangeKey];

        if (Array.isArray(dataForRange)) {
          dataForRange.forEach(entry => {
            const clientType = entry.ClientType;

            if (!clientsData[clientType]) {
              clientsData[clientType] = {
                clientType: clientType,
                clientName: this.getClientNameById(clientType),
                counts: {}
              };
            }

            // Using TotalPages directly from the API response
            const count = entry.TotalPages || 0;
            clientsData[clientType].counts[timeRangeKey] = (clientsData[clientType].counts[timeRangeKey] || 0) + count;
          });
        }
      }

      return Object.values(clientsData).sort((a, b) => {
        const nameA = a.clientName || '';
        const nameB = b.clientName || '';
        return nameA.localeCompare(nameB);
      });
    },

    totalsByTimeRange() {
      const totals = {};

      this.timeRanges.forEach(range => totals[range.key] = 0);

      this.processedItems.forEach(item => {
        this.timeRanges.forEach(range => {
          totals[range.key] += this.getCount(item, range.key);
        });
      });

      return totals;
    },

    /**
     * Calculates the sum of all totals, EXCLUDING 'Received'.
     */
    grandTotal() {
      return this.timeRanges.reduce((sum, range) => {
        // FIX: Skip 'Received' in the grand total sum
        if (range.key === 'Received') return sum;
        return sum + (this.totalsByTimeRange[range.key] || 0);
      }, 0);
    }
  },
  methods: {
    updateItems() {
      const activeClientId = this.$store && this.$store.getters.appActiveClient ? this.$store.getters.appActiveClient.id : 1;

      const payload = {
        client: activeClientId,
        start: this.filters.start,
        end: this.filters.end,
        clients: [],
      };
      this.$emit('updated', payload);
    },

    onRefreshClicked() {
      this.updateItems();
    },

    onPrintClicked() {
      window.print();
    },

    onFilterStartChanged() {
      this.startFilterDisplayValue = Moment(this.filters.start, 'YYYY-MM-DD').format('DD.MM.YYYY');
    },

    onFilterEndChanged() {
      this.endFilterDisplayValue = Moment(this.filters.end, 'YYYY-MM-DD').format('DD.MM.YYYY');
    },

    getCount(item, key) {
      return item.counts && item.counts[key] ? item.counts[key] : 0;
    },

    /**
     * Calculates row total, EXCLUDING 'Received'.
     */
    getRowTotal(item) {
      if (!item.counts) return 0;

      return this.timeRanges.reduce((sum, range) => {
        // FIX: Skip 'Received' in the row sum
        if (range.key === 'Received') return sum;

        return sum + this.getCount(item, range.key);
      }, 0);
    },

    getClientNameById(clientId) {
      if (!this.allClients || !this.allClients[3] || !Array.isArray(this.allClients[3])) {
        return `Неизвестный клиент (ID: ${clientId})`;
      }
      const client = this.allClients[3].find(c => c.value == clientId);
      return client ? client.text : `Неизвестный клиент (ID: ${clientId})`;
    },
  },
  mounted() {
    this.updateItems();
  },
};
</script>

<style scoped>
.report-table {
  width: 100%;
  border-collapse: collapse;
}

.report-table th, .report-table td {
  border: 1px solid #ddd;
  padding: 8px;
}

.report-table th {
  padding-top: 12px;
  padding-bottom: 12px;
  text-align: center;
  background-color: #f2f2f2;
  font-size: 14px;
}

.report-table tr:nth-child(even){background-color: #f9f9f9;}

.report-table tr:hover {
  background-color: #e6f7ff;
}

.total-row {
  background-color: #e3f2fd !important;
  font-size: 14px;
  border-top: 2px solid #90caf9;
}

@media print {
  .v-application .d-print-none {
    display: none !important;
  }
}
</style>